import { Search } from '@mui/icons-material';
import {
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import ModalV2 from '/@/components/bayer/modal-v2';
import ChipLabel from '/@/components/chip-label';
import {
    type parseUserNode,
    sendFieldviewInvitationApi,
    useInvitationsForUserNodes,
    useOwnUserNode,
} from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { InvitationResultItem, UserNodeWithSubtype } from '../invitation-results-modal';
import { tableBorderSx, tableLayoutSx } from '../invitation-results-modal';

const COLUMN_WIDTHS = {
    checkbox: 48,
    productor: '45%',
    segmento: '20%',
    mail: '35%',
} as const;

const GrowerRowCells = ({
    userNode,
    showSegmentoColumn,
    advisorName,
}: {
    userNode: UserNodeWithSubtype;
    showSegmentoColumn: boolean;
    advisorName?: string;
}) => {
    const segmentType = userNode.subtype === 'common' ? 'consultant' : 'rv';
    return (
        <>
            <TableCell sx={{ width: COLUMN_WIDTHS.productor }}>
                <Stack spacing={0.5}>
                    <Typography variant='body1' color='primary'>
                        {userNode.name}
                    </Typography>
                    {userNode.location && (
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {userNode.location}
                        </Typography>
                    )}
                </Stack>
            </TableCell>
            <TableCell sx={{ width: COLUMN_WIDTHS.segmento }}>
                {showSegmentoColumn ? (
                    <ChipLabel label={userNode.subtype === 'common' ? _('Otros') : _('Premium')} type={segmentType} />
                ) : advisorName && advisorName !== '-' ? (
                    <ChipLabel label={advisorName} type='advisor' />
                ) : (
                    <ChipLabel label={_('Sin asignar')} type='gray' />
                )}
            </TableCell>
            <TableCell sx={{ width: COLUMN_WIDTHS.mail }}>
                <Typography variant='body2' color='primary'>
                    {userNode.email || '-'}
                </Typography>
            </TableCell>
        </>
    );
};

const TableHeaderRow = ({
    selectable,
    showSegmentoColumn,
    allSelected,
    someSelected,
    onSelectAll,
}: {
    selectable: boolean;
    showSegmentoColumn: boolean;
    allSelected?: boolean;
    someSelected?: boolean;
    onSelectAll?: (checked: boolean) => void;
}) => (
    <TableRow>
        {selectable ? (
            <TableCell padding='checkbox' sx={{ width: COLUMN_WIDTHS.checkbox }}>
                <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                />
            </TableCell>
        ) : (
            <TableCell padding='checkbox' sx={{ width: COLUMN_WIDTHS.checkbox }}>
                <Checkbox checked disabled sx={{ opacity: 0.6 }} />
            </TableCell>
        )}
        <TableCell sx={{ width: COLUMN_WIDTHS.productor }}>
            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                {_('Productor')}
            </Typography>
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.segmento }}>
            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                {showSegmentoColumn ? _('Segmento') : _('Asesor')}
            </Typography>
        </TableCell>
        <TableCell sx={{ width: COLUMN_WIDTHS.mail }}>
            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                {_('Mail')}
            </Typography>
        </TableCell>
    </TableRow>
);

const InviteRow = ({
    userNode,
    selectedUserNodeIds,
    setSelectedUserNodeIds,
    invitationData,
    showSegmentoColumn,
    advisorName,
}: {
    userNode: ReturnType<typeof parseUserNode>;
    selectedUserNodeIds: Set<number>;
    setSelectedUserNodeIds: (selectedUserNodeIds: Set<number>) => void;
    invitationData: { invitationStatus: string | null; invitationSendedMoreThan7DaysAgo: boolean; disabled: boolean };
    showSegmentoColumn: boolean;
    advisorName?: string;
}) => {
    const handleSelectUserNode = (userNodeId: number, checked: boolean) => {
        if (checked) {
            setSelectedUserNodeIds(new Set([...selectedUserNodeIds, userNodeId]));
        } else {
            const newSelected = new Set(selectedUserNodeIds);
            newSelected.delete(userNodeId);
            setSelectedUserNodeIds(newSelected);
        }
    };

    const disabled = invitationData.disabled;

    return (
        <Tooltip title={disabled ? _('La última invitación fue hace menos de 7 días') : ''}>
            <TableRow
                hover
                sx={[{ cursor: 'pointer' }, disabled && { cursor: 'default', opacity: 0.8 }]}
                onClick={() => !disabled && handleSelectUserNode(userNode.id, !selectedUserNodeIds.has(userNode.id))}
            >
                <TableCell padding='checkbox' sx={{ width: COLUMN_WIDTHS.checkbox }}>
                    <Checkbox
                        checked={selectedUserNodeIds.has(userNode.id)}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleSelectUserNode(userNode.id, e.target.checked);
                        }}
                        disabled={disabled}
                    />
                </TableCell>
                <GrowerRowCells userNode={userNode} showSegmentoColumn={showSegmentoColumn} advisorName={advisorName} />
            </TableRow>
        </Tooltip>
    );
};

const ReadOnlyGrowerRow = ({
    userNode,
    showSegmentoColumn,
    advisorName,
}: {
    userNode: ReturnType<typeof parseUserNode>;
    showSegmentoColumn: boolean;
    advisorName?: string;
}) => (
    <TableRow hover>
        <TableCell padding='checkbox' sx={{ width: COLUMN_WIDTHS.checkbox }}>
            <Checkbox checked disabled sx={{ opacity: 0.6 }} />
        </TableCell>
        <GrowerRowCells userNode={userNode} showSegmentoColumn={showSegmentoColumn} advisorName={advisorName} />
    </TableRow>
);

const GrowersTable = ({
    title,
    userNodes,
    emptyMessage,
    selectedUserNodeIds,
    setSelectedUserNodeIds,
    invitationsData,
    allSelected,
    someSelected,
    onSelectAll,
    showSegmentoColumn,
    advisorNameByNodeId,
}: {
    title: string;
    userNodes: ReturnType<typeof parseUserNode>[];
    emptyMessage: string;
    selectable: true;
    selectedUserNodeIds: Set<number>;
    setSelectedUserNodeIds: (selectedUserNodeIds: Set<number>) => void;
    invitationsData: Record<
        number,
        { invitationStatus: string | null; invitationSendedMoreThan7DaysAgo: boolean; disabled: boolean }
    >;
    allSelected: boolean;
    someSelected: boolean;
    onSelectAll: (checked: boolean) => void;
    showSegmentoColumn: boolean;
    advisorNameByNodeId: Record<number, string>;
}) => (
    <Stack className='mp-mask' spacing={1} width='100%'>
        <Typography variant='caption' color='primary' fontWeight='bold'>
            {title}
        </Typography>
        <TableContainer sx={tableBorderSx}>
            <Table sx={tableLayoutSx}>
                <TableHead>
                    <TableHeaderRow
                        selectable
                        showSegmentoColumn={showSegmentoColumn}
                        allSelected={allSelected}
                        someSelected={someSelected}
                        onSelectAll={onSelectAll}
                    />
                </TableHead>
                <TableBody sx={{ overflow: 'auto' }}>
                    {userNodes.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Typography variant='body1' color='primary'>
                                    {emptyMessage}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        userNodes.map((userNode) => (
                            <InviteRow
                                key={userNode.id}
                                userNode={userNode}
                                selectedUserNodeIds={selectedUserNodeIds}
                                setSelectedUserNodeIds={setSelectedUserNodeIds}
                                invitationData={
                                    invitationsData[userNode.id] ?? {
                                        invitationStatus: null,
                                        invitationSendedMoreThan7DaysAgo: true,
                                        disabled: false,
                                    }
                                }
                                showSegmentoColumn={showSegmentoColumn}
                                advisorName={advisorNameByNodeId[userNode.id]}
                            />
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    </Stack>
);

const GrowersTableReadOnly = ({
    title,
    userNodes,
    emptyMessage,
    showSegmentoColumn,
    advisorNameByNodeId,
}: {
    title: string;
    userNodes: ReturnType<typeof parseUserNode>[];
    emptyMessage: string;
    showSegmentoColumn: boolean;
    advisorNameByNodeId: Record<number, string>;
}) => (
    <Stack className='mp-mask' spacing={1} width='100%'>
        <Typography variant='caption' color='primary' fontWeight='bold'>
            {title}
        </Typography>
        <TableContainer sx={tableBorderSx}>
            <Table sx={tableLayoutSx}>
                <TableHead>
                    <TableHeaderRow selectable={false} showSegmentoColumn={showSegmentoColumn} />
                </TableHead>
                <TableBody sx={{ overflow: 'auto' }}>
                    {userNodes.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Typography variant='body1' color='primary'>
                                    {emptyMessage}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        userNodes.map((userNode) => (
                            <ReadOnlyGrowerRow
                                key={userNode.id}
                                userNode={userNode}
                                showSegmentoColumn={showSegmentoColumn}
                                advisorName={advisorNameByNodeId[userNode.id]}
                            />
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    </Stack>
);

const InviteGrowerToFieldviewStep = ({
    userNodesFilteredByFieldview,
    userNodesConnectedWithFieldview,
    search,
    setSearch,
    selectedUserNodeIds,
    setSelectedUserNodeIds,
    userNodesQuery,
    trackEntrySource,
    createOwnUserField,
    onBack,
    onInvitationSent,
    bodyProps,
    actionsProps,
    onClose,
    open,
}: {
    userNodesFilteredByFieldview: ReturnType<typeof parseUserNode>[];
    userNodesConnectedWithFieldview: ReturnType<typeof parseUserNode>[];
    search: string;
    setSearch: (search: string) => void;
    selectedUserNodeIds: Set<number>;
    setSelectedUserNodeIds: (selectedUserNodeIds: Set<number>) => void;
    userNodesQuery: { data?: { results: ReturnType<typeof parseUserNode>[] } };
    trackEntrySource?: Parameters<typeof mpTrackEvent<'Field create began'>>['1']['entry_source'];
    createOwnUserField: boolean;
    onBack: () => void;
    onInvitationSent: (
        results: InvitationResultItem[],
        meta: { showSegmentoColumn: boolean; advisorNameByNodeId: Record<number, string> }
    ) => void;
    bodyProps?: React.ComponentProps<typeof ModalV2>['bodyProps'];
    actionsProps?: React.ComponentProps<typeof ModalV2>['actionsProps'];
    onClose: () => void;
    open: boolean;
}) => {
    const country = useSelector(getSessionCountry);
    const { mutate } = useSWRConfig();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isRv = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();
    const showSegmentoColumn = isAdvisor;

    const ownUserNodeQuery = useOwnUserNode();
    const rvUserNodeId = isRv ? ownUserNodeQuery.data?.id : undefined;

    const advisorIdToName = useMemo(() => {
        const results = userNodesQuery.data?.results ?? [];
        const advisorNodes = results.filter((n) => (n as { type?: string }).type === 'advisor');
        return Object.fromEntries(advisorNodes.map((n) => [n.id, n.name]));
    }, [userNodesQuery.data?.results]);

    const advisorNameByNodeId = useMemo(() => {
        if (showSegmentoColumn || rvUserNodeId == null) return {};
        const map: Record<number, string> = {};
        for (const node of [...userNodesFilteredByFieldview, ...userNodesConnectedWithFieldview] as (ReturnType<
            typeof parseUserNode
        > & { parents?: number[] })[]) {
            const parents = node.parents ?? [];
            const advisorId = parents.find((id) => id !== rvUserNodeId);
            map[node.id] = advisorId != null ? (advisorIdToName[advisorId] ?? '-') : '-';
        }
        return map;
    }, [
        showSegmentoColumn,
        rvUserNodeId,
        userNodesFilteredByFieldview,
        userNodesConnectedWithFieldview,
        advisorIdToName,
    ]);

    const filteredUserNodes = useMemo(() => {
        return userNodesFilteredByFieldview.filter((userNode) =>
            userNode.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [userNodesFilteredByFieldview, search]);

    // Get invitation data for all filtered user nodes
    const invitationsData = useInvitationsForUserNodes(filteredUserNodes);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            // Only select enabled rows
            const enabledUserNodeIds = filteredUserNodes
                .filter((userNode) => !invitationsData[userNode.id]?.disabled)
                .map((userNode) => userNode.id);

            setSelectedUserNodeIds(new Set(enabledUserNodeIds));
        } else {
            setSelectedUserNodeIds(new Set());
        }
    };

    // Calculate selection state based on enabled rows only
    const enabledUserNodes = useMemo(() => {
        return filteredUserNodes.filter((userNode) => !invitationsData[userNode.id]?.disabled);
    }, [filteredUserNodes, invitationsData]);

    const allSelected =
        enabledUserNodes &&
        enabledUserNodes.length > 0 &&
        enabledUserNodes.every((userNode) => selectedUserNodeIds.has(userNode.id));
    const someSelected = enabledUserNodes?.some((userNode) => selectedUserNodeIds.has(userNode.id));

    // If there is one available to be selected, select it
    useEffect(() => {
        if (enabledUserNodes.length === 1 && enabledUserNodes[0]) {
            setSelectedUserNodeIds(new Set([enabledUserNodes[0].id]));
        } else if (enabledUserNodes.length === 0) {
            setSelectedUserNodeIds(new Set());
        }
    }, [enabledUserNodes, setSelectedUserNodeIds]);

    const handleSubmit = async () => {
        if (!country?.code) return;
        if (trackEntrySource) {
            mpTrackEvent('Field create began', {
                entry_source: trackEntrySource,
                create_own_user_field: createOwnUserField,
            });
        }

        const orderedIds = Array.from(selectedUserNodeIds);
        const userNodeById = new Map(
            (userNodesQuery.data?.results ?? []).map((n) => [n.id, n as InvitationResultItem['userNode']])
        );

        setIsSubmitting(true);
        try {
            const settled = await Promise.allSettled(
                orderedIds.map((userNodeId) => {
                    const userNode = userNodeById.get(userNodeId);
                    if (!userNode) throw new Error(_('Productor no encontrado'));
                    mpTrackEvent('FieldView Invitation Sent', {});
                    return sendFieldviewInvitationApi({
                        invitedUserName: userNode.name,
                        invitedUserEmail: userNode.email,
                        sharedNodeId: userNode.id,
                        countryCode: country.code,
                    });
                })
            );

            const results: InvitationResultItem[] = settled
                .map((outcome, i) => {
                    const id = orderedIds[i];
                    const userNode = id !== undefined ? userNodeById.get(id) : undefined;
                    if (!userNode) return null;
                    if (outcome.status === 'fulfilled') {
                        return { userNode, status: 'success' as const };
                    }

                    return {
                        userNode,
                        status: 'error' as const,
                        errorMessage: _('Este productor ya compartió su operación con otro usuario'),
                    };
                })
                .filter((r): r is NonNullable<typeof r> => r !== null);

            if (results.some((r) => r.status === 'success')) {
                mutate(
                    (cacheKey: unknown) =>
                        typeof cacheKey === 'object' &&
                        cacheKey !== null &&
                        'key' in cacheKey &&
                        (cacheKey as { key?: string }).key === 'useListInvitations'
                );
            }

            onInvitationSent(results, { showSegmentoColumn, advisorNameByNodeId });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Solicitar al productor acceder a sus lotes de FieldView')}
            actions={
                <>
                    <Button variant='text' color='secondary' onClick={onBack}>
                        {_('Atras')}
                    </Button>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={handleSubmit}
                        disabled={selectedUserNodeIds.size === 0 || isSubmitting}
                    >
                        {_('Enviar solicitudes y finalizar')}
                    </Button>
                </>
            }
            bodyProps={bodyProps}
            actionsProps={actionsProps}
            maxWidth='md'
        >
            <Typography variant='subtitle1' color='primary'>
                {_(
                    'Seleccioná los productores a los que querés pedirles autorización para conectar con FieldView. Les vamos a enviar un mail para que acepten la conexión.'
                )}
            </Typography>
            <TextField
                InputProps={{
                    startAdornment: <Search />,
                }}
                placeholder={_('Busca el productor por el nombre')}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                fullWidth
            />
            {userNodesFilteredByFieldview.length > 0 && (
                <GrowersTable
                    title={_('Productores a invitar')}
                    userNodes={filteredUserNodes}
                    emptyMessage={_('No se encontraron productores')}
                    selectable
                    selectedUserNodeIds={selectedUserNodeIds}
                    setSelectedUserNodeIds={setSelectedUserNodeIds}
                    invitationsData={invitationsData}
                    allSelected={allSelected}
                    someSelected={someSelected}
                    onSelectAll={handleSelectAll}
                    showSegmentoColumn={showSegmentoColumn}
                    advisorNameByNodeId={advisorNameByNodeId}
                />
            )}
            {userNodesConnectedWithFieldview.length > 0 && (
                <GrowersTableReadOnly
                    title={_('Productores conectados con FieldView')}
                    userNodes={userNodesConnectedWithFieldview}
                    emptyMessage={_('No hay productores conectados con FieldView')}
                    showSegmentoColumn={showSegmentoColumn}
                    advisorNameByNodeId={advisorNameByNodeId}
                />
            )}
        </ModalV2>
    );
};

export default InviteGrowerToFieldviewStep;
